/// <amd-module name="VSSUI/Components/SplitButton/SplitButton" />

import * as React from "react";
import { Button } from "VSSUI/Button";
import { Expandable, IExpandableChildProps } from "VSSUI/Expandable";
import { FocusZone, FocusZoneContext, FocusZoneDirection } from "VSSUI/FocusZone";
import { IconSize } from "VSSUI/Icon";
import { ContextualMenu, IMenuItem } from "VSSUI/Menu";
import * as Resources from "VSSUI/Resources.Widgets";
import { css } from "VSSUI/Util";
import { Location } from "VSSUI/Utilities/Position";
import { ISplitButtonProps } from "./SplitButton.Props";

export function SplitButton(props: ISplitButtonProps) {
    const anchor = React.useRef<HTMLDivElement>() as React.MutableRefObject<HTMLDivElement>;

    const expandableRef = props.expandableRef || (React.useRef<Expandable>() as React.MutableRefObject<Expandable>);

    const { buttonRef, buttonProps, className, disabled, menuButtonProps, primary, subtle } = props;

    const renderCallout = () => {
        const contextualMenuProps =
            typeof props.menuButtonProps.contextualMenuProps === "function"
                ? props.menuButtonProps.contextualMenuProps()
                : props.menuButtonProps.contextualMenuProps;

        return (
            <ContextualMenu
                anchorElement={anchor.current!}
                anchorOrigin={{ horizontal: Location.end, vertical: Location.end }}
                menuOrigin={{ horizontal: Location.end, vertical: Location.start }}
                menuProps={contextualMenuProps.menuProps}
                onActivate={(menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    if (contextualMenuProps.onActivate) {
                        contextualMenuProps.onActivate(menuItem, event);
                    }
                    expandableRef.current && expandableRef.current.collapse();
                }}
                onDismiss={() => expandableRef.current && expandableRef.current.collapse()}
            />
        );
    };

    return (
        <FocusZoneContext.Consumer>
            {(zoneContext): JSX.Element => {
                let button = (
                    <div className={css(className, "bolt-split-button flex-stretch inline-flex-row")} ref={anchor}>
                        <Button
                            {...buttonProps}
                            className={css("bolt-split-button-main", buttonProps.className)}
                            disabled={disabled || buttonProps.disabled}
                            focusZoneId={zoneContext.direction === FocusZoneDirection.Vertical ? zoneContext.focuszoneId : undefined}
                            primary={primary}
                            subtle={subtle}
                            ref={buttonRef}
                        />
                        <div className={css("bolt-split-button-divider flex-noshrink", primary && "primary", disabled && "disabled")} />
                        <Expandable
                            disabled={disabled}
                            onCollapse={menuButtonProps.onCollapse}
                            onExpand={menuButtonProps.onExpand}
                            ref={expandableRef}
                            renderCallout={props.renderCallout || renderCallout}
                        >
                            {(expandableProps: IExpandableChildProps) => {
                                return (
                                    <div className="inline-flex-row" onMouseDown={expandableProps.onMouseDown} onKeyDown={expandableProps.onKeyDown}>
                                        <Button
                                            ariaExpanded={expandableProps.expanded}
                                            ariaLabel={Resources.MoreActions}
                                            iconProps={{
                                                iconName: "ChevronDownMed",
                                                size: IconSize.small
                                            }}
                                            text={undefined}
                                            {...menuButtonProps}
                                            className={css(
                                                "bolt-split-button-option flex-self-stretch body-s",
                                                menuButtonProps.className,
                                                expandableProps.expanded && "active"
                                            )}
                                            disabled={disabled}
                                            onClick={expandableProps.onClick}
                                            primary={primary}
                                            subtle={subtle}
                                        />
                                    </div>
                                );
                            }}
                        </Expandable>
                    </div>
                );
                if (zoneContext.direction !== FocusZoneDirection.Horizontal) {
                    button = <FocusZone direction={FocusZoneDirection.Horizontal}>{button}</FocusZone>;
                }
                return button;
            }}
        </FocusZoneContext.Consumer>
    );
}
